import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import {
  Alert, Button, TextInput, Textarea,
} from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import prepareTranslate from '@/helpers/dictionary';
import Modal from '@/components/DetailsModal';
import EmailBodyEditor from '../EmailBodyEditor';
import styles from './index.module.css';

/**
 * Resends an existing email. Every send is kept as an additional row in the
 * agent's email log, so the full history stays visible, and a reason for the
 * resend is required.
 */
const ResendAgentEmail = (props) => {
  const {
    email,
    agentEmail,
    opened,
    close,
    onSend,
    loading,
  } = props;

  const t = prepareTranslate();
  const contentRef = useRef(null);

  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [recipients, setRecipients] = useState('');
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState(null);
  const [keptFiles, setKeptFiles] = useState([]);
  const [newFiles, setNewFiles] = useState([]);

  useEffect(() => {
    if (!opened) return;

    setSubject(email?.name || '');
    setContent(email?.content || '');
    setRecipients(agentEmail || '');
    setReason('');
    setReasonError(null);
    setKeptFiles(email?.files?.filter((file) => file.path) || []);
    setNewFiles([]);
  }, [opened, email, agentEmail]);

  const handleSend = () => {
    if (!reason.trim()) {
      setReasonError(t('validations.required'));
      return;
    }

    onSend({
      subject,
      // The body is html - read it back from the editable element as it stands.
      content: contentRef.current?.innerHTML ?? content,
      recipients: recipients.split(',').map((item) => item.trim()).filter(Boolean),
      resendReason: reason.trim(),
      keptFiles,
      newFiles,
    });
  };

  const actions = [
    <Button key='send' onClick={handleSend} loading={loading} w={120}>
      {t('components.viewAgentEmails.resend.send')}
    </Button>,
    <Button key='cancel' variant='outline' onClick={close} w={120}>
      {t('global.cancel')}
    </Button>,
  ];

  return (
    <Modal
      opened={opened}
      close={close}
      actions={actions}
      label={t('components.viewAgentEmails.resend.label')}
    >
      <Alert
        icon={<IconInfoCircle />}
        color='blue'
        variant='light'
        className={styles.notice}
      >
        {t('components.viewAgentEmails.resend.notice')}
      </Alert>

      <Textarea
        label={t('components.viewAgentEmails.resend.reason')}
        placeholder={t('components.viewAgentEmails.resend.reasonPlaceholder')}
        value={reason}
        error={reasonError}
        withAsterisk
        autosize
        minRows={2}
        className={styles.field}
        onChange={(event) => {
          setReason(event.currentTarget.value);
          setReasonError(null);
        }}
      />

      <TextInput
        label={t('components.viewAgentEmails.resend.recipients')}
        placeholder={t('components.viewAgentEmails.resend.recipientsPlaceholder')}
        value={recipients}
        className={styles.field}
        onChange={(event) => setRecipients(event.currentTarget.value)}
      />

      <TextInput
        label={t('components.viewAgentEmails.resend.subject')}
        value={subject}
        className={styles.field}
        onChange={(event) => setSubject(event.currentTarget.value)}
      />

      <EmailBodyEditor
        label={t('components.viewAgentEmails.resend.content')}
        content={content}
        contentRef={contentRef}
        keptFiles={keptFiles}
        newFiles={newFiles}
        onAddFiles={(previews) => setNewFiles((prev) => [...prev, ...previews])}
        onRemoveKeptFile={(index) => setKeptFiles(
          (prev) => prev.filter((_, i) => i !== index),
        )}
        onRemoveNewFile={(index) => setNewFiles((prev) => prev.filter((_, i) => i !== index))}
      />
    </Modal>
  );
};

ResendAgentEmail.propTypes = {
  email: PropTypes.object,
  agentEmail: PropTypes.string,
  opened: PropTypes.bool,
  close: PropTypes.func,
  onSend: PropTypes.func,
  loading: PropTypes.bool,
};

export default ResendAgentEmail;
